import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { PageHeader, Badge } from "@/components/ui/PageHeader";
import ClaimButton from "@/components/ClaimButton";
import { getOrganizerDirectoryEntry } from "@/api/event-organizers";
import { ORG_TYPE_LABEL } from "./Directory";

export default function OrganizerDirectoryDetail() {
  const { id = "" } = useParams();

  const { data, isLoading, error } = useQuery({
    queryKey: ["organizer-directory-entry", id],
    queryFn: () => getOrganizerDirectoryEntry(id),
    enabled: !!id,
  });

  const o = data?.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title={o?.orgName ?? "Organizer"}
        subtitle="A researched organizer from the directory. Claim it if you run this org."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Event Organizers", href: "/lab/event-organizers" },
          { label: "Directory", href: "/lab/event-organizers/directory" },
          { label: o?.orgName ?? "…" },
        ]}
      />

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : error || !o ? (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
          Directory entry not found.
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 space-y-5 max-w-2xl">
          {/* Identity */}
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-100 to-accent-100 flex items-center justify-center text-2xl shrink-0">
              🎪
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-semibold text-gray-900">{o.orgName}</h2>
                {o.acronymAliases.map((a) => (
                  <span key={a} className="text-xs text-gray-400">
                    {a}
                  </span>
                ))}
              </div>
              <p className="text-sm text-gray-500 mt-0.5">
                {[o.city, o.state].filter(Boolean).join(", ")}
                {o.metroArea ? ` · ${o.metroArea}` : ""}
              </p>
            </div>
          </div>

          {/* Facts */}
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            {o.orgType && (
              <Fact label="Type">
                <Badge variant="gray">{ORG_TYPE_LABEL[o.orgType]}</Badge>
              </Fact>
            )}
            {o.regionOrLanguage && <Fact label="Region / Language">{o.regionOrLanguage}</Fact>}
            {o.foundedYear && <Fact label="Founded">{o.foundedYear}</Fact>}
            {o.keyPerson && <Fact label="Key person">{o.keyPerson}</Fact>}
            {o.websiteUrl && (
              <Fact label="Website">
                <a
                  href={o.websiteUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary-600 hover:underline break-all"
                >
                  {o.websiteUrl.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "")}
                </a>
              </Fact>
            )}
            {o.primarySocialUrl && (
              <Fact label="Social">
                <a
                  href={o.primarySocialUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary-600 hover:underline break-all"
                >
                  {o.primarySocialUrl.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "")}
                </a>
              </Fact>
            )}
          </dl>

          {/* Claim / claimed state */}
          <div className="pt-4 border-t border-gray-100">
            {o.isClaimed ? (
              <div className="text-sm text-gray-600 flex items-center gap-2">
                <Badge variant="green">claimed</Badge>
                This organizer is already live.
                {o.eventOrganizerId && (
                  <Link
                    to={`/lab/event-organizers`}
                    className="text-primary-600 hover:underline"
                  >
                    View organizers →
                  </Link>
                )}
              </div>
            ) : (
              <>
                <p className="text-xs text-gray-500 mb-2">
                  Run this organization? Claim it — on admin approval your live
                  organizer page is created, pre-filled from this entry
                  (including its legal record). It still goes through the normal
                  verification step before it can take payments.
                </p>
                <ClaimButton
                  entityType="event_organizer_prospect"
                  entityId={o.id}
                  label="organizer"
                />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-gray-400 uppercase tracking-wide">{label}</dt>
      <dd className="text-gray-800 mt-0.5">{children}</dd>
    </div>
  );
}
